// Browser-side TikZ → SVG renderer.
//
// The public TikZJax CDN (https://tikzjax.com/v1/tikzjax.js) auto-processes
// every <script type="text/tikz"> in the document on DOMContentLoaded, but
// it does not reliably expose `window.tikzjax.process(...)` for ad-hoc calls
// against scripts injected after page load. Earlier versions of this file
// tried to use that global; the symptom in production was a 15 s timeout
// followed by "TikZJax failed to initialize".
//
// The proven path is one isolated iframe per render: srcdoc gives the iframe
// a fresh DOMContentLoaded, so TikZJax's auto-process fires, and the parent
// just polls the iframe body for the resulting <svg>. Each render is fully
// isolated — a stuck or broken render in one diagram can't poison the next.

const DEFAULT_RENDER_TIMEOUT_MS = 25000;
const POLL_INTERVAL_MS = 200;

const SCRIPT_TIKZ_RE =
  /<script([^>]*)type=(?:"text\/tikz"|'text\/tikz')([^>]*)>([\s\S]*?)<\/script>/gi;

const isBrowser = () =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

// Render a single TikZ snippet to an SVG string. Rejects on timeout, load
// failure, or AbortSignal abort. Strips control bytes the same way the
// server's sanitize_tikz_code drops them up-front, which keeps the cache
// hash stable across client and server.
//
// Memory note: each render mounts an iframe that boots a fresh TikZJax
// WebAssembly instance (~16MB linear memory). Cleanup is aggressive — when
// the render settles or the caller aborts, the iframe is navigated to
// about:blank *before* being removed from the DOM so the browser tears
// down the WASM heap synchronously instead of waiting for GC. Without
// this, repeatedly opening and closing the diagram viewer stacked zombie
// iframes and exhausted browser memory (Safari/Chrome would reload the
// tab "because it was using significant memory").
export const renderTikzToSvg = (
  tikzCode,
  { timeoutMs = DEFAULT_RENDER_TIMEOUT_MS, signal = null } = {},
) => {
  if (!isBrowser()) {
    return Promise.reject(new Error('TikZJax requires a browser environment'));
  }
  const cleaned = String(tikzCode || '').replace(/[^\x09\x0A\x0D\x20-\x7E]/g, '');
  if (!cleaned.trim()) {
    return Promise.reject(new Error('Empty TikZ source'));
  }
  if (signal && signal.aborted) {
    return Promise.reject(new Error('Render aborted'));
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId = null;
    let pollId = null;
    let iframe = null;
    let onLoad = null;
    let onError = null;
    let onAbort = null;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (pollId) {
        clearInterval(pollId);
        pollId = null;
      }
      if (signal && onAbort) {
        try { signal.removeEventListener('abort', onAbort); } catch (_e) { /* ignore */ }
        onAbort = null;
      }
      if (iframe) {
        if (onLoad) {
          try { iframe.removeEventListener('load', onLoad); } catch (_e) { /* ignore */ }
        }
        if (onError) {
          try { iframe.removeEventListener('error', onError); } catch (_e) { /* ignore */ }
        }
        // Force tear-down of the iframe's document (including the TikZJax
        // WASM heap) before detaching: replace srcdoc with about:blank so
        // the browser unloads the old document synchronously rather than
        // waiting for GC after removeChild.
        try { iframe.removeAttribute('srcdoc'); } catch (_e) { /* ignore */ }
        try { iframe.src = 'about:blank'; } catch (_e) { /* ignore */ }
        if (iframe.parentNode) {
          try { iframe.parentNode.removeChild(iframe); } catch (_e) { /* ignore */ }
        }
        onLoad = null;
        onError = null;
        iframe = null;
      }
    };

    const finishOk = (svgHtml) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(svgHtml);
    };

    const finishErr = (msg) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error(msg));
    };

    const tryExtract = () => {
      const doc = iframe && iframe.contentDocument;
      if (!doc || !doc.body) return null;
      const svg = doc.body.querySelector('svg');
      return svg && svg.outerHTML ? svg.outerHTML : null;
    };

    // Render in an offscreen iframe. TikZJax auto-processes the inner
    // <script type="text/tikz"> when DOMContentLoaded fires inside the
    // iframe — we just poll for the SVG to appear in the body.
    iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
    iframe.style.position = 'absolute';
    iframe.style.left = '-99999px';
    iframe.style.top = '0';
    iframe.style.width = '800px';
    iframe.style.height = '800px';
    iframe.style.border = 'none';
    iframe.style.opacity = '0';
    iframe.style.pointerEvents = 'none';

    const safeMarkup = cleaned.replace(/<\/script>/gi, '<\\/script>');
    iframe.srcdoc = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <link rel="stylesheet" type="text/css" href="https://tikzjax.com/v1/fonts.css">
  <style>
    html, body { margin: 0; padding: 0; background: transparent; }
    body { font-family: Arial, sans-serif; padding: 10px; }
    svg { display: inline-block; max-width: 100%; }
  </style>
</head>
<body>
  <script type="text/tikz">${safeMarkup}</script>
  <script src="https://tikzjax.com/v1/tikzjax.js"></script>
</body>
</html>`;

    onLoad = () => {
      if (settled) return;
      // Start polling once the iframe document is in place. TikZJax may
      // take a few seconds (WASM init + TeX compile); the outer timeout
      // bounds total wait.
      const immediate = tryExtract();
      if (immediate) { finishOk(immediate); return; }
      pollId = setInterval(() => {
        const svgHtml = tryExtract();
        if (svgHtml) finishOk(svgHtml);
      }, POLL_INTERVAL_MS);
    };

    onError = () => finishErr('TikZJax script failed to load');

    iframe.addEventListener('load', onLoad);
    iframe.addEventListener('error', onError);

    timeoutId = setTimeout(() => {
      const late = tryExtract();
      if (late) finishOk(late);
      else finishErr(`Render timed out after ${timeoutMs}ms`);
    }, timeoutMs);

    document.body.appendChild(iframe);

    // Wire abort *after* the iframe is parented so the cleanup path always
    // has a parent to remove from. AbortController.abort() fires the abort
    // event synchronously; if the signal aborted while we were setting up,
    // we still settle correctly.
    if (signal) {
      onAbort = () => finishErr('Render aborted');
      signal.addEventListener('abort', onAbort);
      if (signal.aborted) finishErr('Render aborted');
    }
  });
};

// Walk an HTML string, render every <script type="text/tikz">…</script> in
// sequence (TikZJax is slow enough on WASM init that parallel iframes can
// thrash mobile WebView memory), and return the HTML with each script
// replaced by the rendered SVG markup. Each successful render calls
// onRendered({ tikzCode, svgHtml }) so the caller can persist the result
// (write-back to the worksheet's tikz-svg-cache, etc).
//
// Failures are tolerated: a script that fails to render is left in place so
// the legacy in-page TikZJax can still take a swing at it, rather than
// silently dropping the diagram from the export.
export const prerenderTikzInHtml = async (
  html,
  { onRendered, timeoutMs = DEFAULT_RENDER_TIMEOUT_MS, signal = null } = {},
) => {
  if (typeof html !== 'string' || html.length === 0) {
    return { html: html || '', rendered: 0, failed: 0 };
  }
  if (!isBrowser() || !html.includes('text/tikz')) {
    return { html, rendered: 0, failed: 0 };
  }

  const matches = [];
  SCRIPT_TIKZ_RE.lastIndex = 0;
  let m;
  // eslint-disable-next-line no-cond-assign
  while ((m = SCRIPT_TIKZ_RE.exec(html)) !== null) {
    matches.push({ fullMatch: m[0], tikzCode: m[3] || '' });
  }
  if (matches.length === 0) {
    return { html, rendered: 0, failed: 0 };
  }

  const replacements = new Map(); // fullMatch → svgMarkup
  let rendered = 0;
  let failed = 0;

  for (const { fullMatch, tikzCode } of matches) {
    if (signal && signal.aborted) break;
    if (replacements.has(fullMatch)) continue;
    if (!tikzCode.trim()) continue;
    try {
      const svgHtml = await renderTikzToSvg(tikzCode, { timeoutMs, signal });
      replacements.set(fullMatch, svgHtml);
      rendered += 1;
      if (typeof onRendered === 'function') {
        try {
          onRendered({ tikzCode, svgHtml });
        } catch (_listenerError) {
          // listener is the caller's problem, not ours
        }
      }
    } catch (_renderError) {
      failed += 1;
    }
  }

  if (replacements.size === 0) {
    return { html, rendered, failed };
  }

  let updated = html;
  for (const [fullMatch, svgHtml] of replacements.entries()) {
    const wrapped = `<div class="diagram diagram-tikz-prerendered" style="display:block;text-align:center;margin:0.5rem 0;">${svgHtml}</div>`;
    updated = updated.split(fullMatch).join(wrapped);
  }
  return { html: updated, rendered, failed };
};

// Kept as a no-op for back-compat with any module that imported it from an
// earlier revision. The current renderer needs no shared singleton because
// each render runs in its own iframe.
export const loadTikZJaxOnce = () => Promise.resolve();
